import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import TextInput from '@narvar/nth-text-input';
import { MediumHeader } from '@narvar/nth-header';
import SelectList from '@narvar/nth-select-list';
import styled from 'styled-components';
import { updateUserShippingAddress, setShippingAddressFormInvalid } from '../../actions/addresses';
import DEFAULT_COPY from '../../constants/defaultCopy';
import { isAddressValid, buildAddressFields } from '../../lib/addresses';
import { getCountryStates } from '../../lib/geolocalizationUtils';
import localizedCountries from '../../lib/localizedCountries';
import { PICKUP } from '../../constants/returnMethods';

export const mapStateToProps = ({
  addresses: { shipTo, userShippingAddress, notificationMsg } = {},
  language,
  page: { countryCode, languageCode, giftOrder },
  returnOptions: { returnOptionMethods, selectedOptionMethodKey, selectedOptionId },
  returnItems: { hasExchange },
  returnsSettings: {
    returns: { formShippingMethodConfig, displayFormIfExchangeIncluded, selectedCountries } = {},
  } = {},
}) => ({
  formShippingMethodConfig,
  language,
  countryCode,
  languageCode,
  notificationMsg,
  selectedMethod: returnOptionMethods[selectedOptionMethodKey],
  shipTo,
  userShippingAddress,
  hasExchangeOverride: displayFormIfExchangeIncluded && hasExchange,
  selectedCountries,
  selectedOptionId,
  giftOrder,
});

const Container = styled.div`
  text-align: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  > h4 {
    margin: 1.5rem auto;
    &[data-test='address-validation-error'] {
      margin-bottom: 0;
    }
  }
  .sidebar-input {
    margin: 0.5rem 0;
    min-width: 0;
  }
  .sidebar-select {
    padding: 0 0.5rem;
  }
  > button {
    margin: 1.5rem 2rem;
  }
`;

const ShippingAddressForm = ({
  dispatch,
  language,
  countryCode,
  languageCode,
  selectedMethod,
  userShippingAddress,
  shipTo,
  notificationMsg,
  formShippingMethodConfig,
  hasExchangeOverride,
  selectedCountries,
  selectedOptionId,
  giftOrder,
}) => {
  const { [languageCode]: DEFAULT_LANG_COPY = {} } = DEFAULT_COPY;

  const hideAddressForm =
    selectedMethod &&
    selectedOptionId !== PICKUP &&
    isAddressValid(shipTo, countryCode) &&
    !hasExchangeOverride &&
    !giftOrder;

  React.useEffect(() => {
    dispatch(setShippingAddressFormInvalid(!hideAddressForm));
  }, [hideAddressForm]);

  if (hideAddressForm) {
    return null;
  }

  const change = (key, event) => {
    const shippingAddress = {
      ...userShippingAddress,
      [key]: event.target.value.trim(),
    };
    dispatch(
      updateUserShippingAddress({
        userShippingAddress: shippingAddress,
      }),
    );
    const isValid = isAddressValid(shippingAddress, countryCode);
    dispatch(setShippingAddressFormInvalid(!isValid));
  };

  const selectInputOptions = (key) => {
    if (key === 'country') {
      const countries = localizedCountries(languageCode);
      return selectedCountries && selectedCountries.length
        ? countries.filter((country) => selectedCountries.includes(country.value))
        : countries;
    }
    if (key === 'state') {
      return getCountryStates(countryCode).map((state) => ({
        value: state.state_name,
        label: state.state_name,
      }));
    }
    return [];
  };

  const getElement = (elementProps) => {
    const displayTitle = `${language[elementProps.languageKey] || elementProps.placeholder}
    ${elementProps.required ? '*' : ''}`;
    switch (elementProps.type) {
      case 'select': {
        const options = selectInputOptions(elementProps.key);
        if (options.length === 1) {
          const defaultValue = options[0]?.value || '';
          if (!userShippingAddress[elementProps.key]) {
            dispatch(
              updateUserShippingAddress({
                userShippingAddress: {
                  ...userShippingAddress,
                  [elementProps.key]: defaultValue,
                },
              }),
            );
          }
        }
        return (
          <div
            className="fs-exclude"
            style={{
              width: `${elementProps.width}%`,
              height: '3.125rem',
              fontWeight: userShippingAddress[elementProps.key] ? 'normal' : '700',
            }}
          >
            <SelectList
              className="sidebar-select sidebar-input"
              id={elementProps.key}
              key={`address-form-${elementProps.key}`}
              value={userShippingAddress[elementProps.key]}
              onChange={(e) => change(elementProps.key, e)}
              s={{
                height: '100%',
                width: '100%',
                color: userShippingAddress[elementProps.key] ? '#000' : '#a8a8a8',
              }}
            >
              <option value={displayTitle} selected disabled>
                {displayTitle}
              </option>
              {options.map((option, index) => (
                <option
                  key={`address-form-option-${option.value}`}
                  value={option.value}
                  data-test={`sidebar-select-option-${index}`}
                >
                  {option.label}
                </option>
              ))}
            </SelectList>
          </div>
        );
      }
      case 'text':
      default:
        return (
          <TextInput
            className={`fs-exclude sidebar-input ${
              userShippingAddress[elementProps.key] ? 'filled-input' : ''
            }`}
            id={elementProps.key}
            key={`address-form-${elementProps.key}`}
            s={{ width: `${elementProps.width}%` }}
            type="text"
            onChange={(e) => change(elementProps.key, e)}
            defaultValue={userShippingAddress[elementProps.key]}
            label={displayTitle}
            labelStyle={{ color: '#a8a8a8' }}
            inputStyle={{ color: 'black' }}
          />
        );
    }
  };
  return (
    <Container>
      <MediumHeader>
        {language['returns.step_2.shipping_form_question'] ||
          DEFAULT_LANG_COPY['returns.step_2.shipping_form_question']}
      </MediumHeader>
      {buildAddressFields({ countryCode, addressConfig: formShippingMethodConfig }).map(
        (elementProps) => getElement(elementProps),
      )}
      {notificationMsg ? (
        <MediumHeader
          data-test="address-validation-error"
          s={{
            color: '#d40511',
            fontWeight: 'normal',
            fontSize: '0.875rem',
            marginBottom: 0,
          }}
        >
          {notificationMsg}
        </MediumHeader>
      ) : null}
    </Container>
  );
};

ShippingAddressForm.defaultProps = {
  languageCode: 'en',
  countryCode: 'US',
  selectedMethod: {},
  userShippingAddress: {},
  hasExchangeOverride: false,
  selectedCountries: [],
  giftOrder: false,
};

ShippingAddressForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  formShippingMethodConfig: PropTypes.array.isRequired,
  language: PropTypes.object.isRequired,
  countryCode: PropTypes.string,
  languageCode: PropTypes.string,
  notificationMsg: PropTypes.string.isRequired,
  selectedMethod: PropTypes.object,
  shipTo: PropTypes.object.isRequired,
  userShippingAddress: PropTypes.object,
  hasExchangeOverride: PropTypes.bool,
  selectedCountries: PropTypes.object,
  selectedOptionId: PropTypes.string.isRequired,
  giftOrder: PropTypes.bool,
};

export default connect(mapStateToProps)(ShippingAddressForm);
export { ShippingAddressForm };
